<template>
    <div ref="block" class="bouncing-block">
        {{ text }}
    </div>
</template>

<script>
export default {
    name: 'BouncingBlock',

    props: {
        text: {
            type: String,
            default: '🚀 Hello Word'
        },
        speed: {
            type: Number,
            default: 360
        }
    },

    data() {
        return {
            vx: 0,
            vy: 0
        }
    },

    created() {
        this.state = {
            x: 0,
            y: 0,
            maxX: 0,
            maxY: 0,
            rafId: null,
            lastTime: 0,
            targetX: null,
            targetY: null
        }
    },

    mounted() {
        this.measure()
        this.state.x = this.state.maxX / 2
        this.state.y = this.state.maxY / 2
        this.randomDirection()
        this.render()

        window.addEventListener('resize', this.handleResize)
        document.addEventListener('visibilitychange', this.handleVisibility)
        // pointerdown 在手机和 PC 上都立即触发，无 300ms 延迟
        window.addEventListener('pointerdown', this.handlePointerDown)

        this.start()
    },

    beforeDestroy() {
        this.stop()
        window.removeEventListener('resize', this.handleResize)
        document.removeEventListener('visibilitychange', this.handleVisibility)
        window.removeEventListener('pointerdown', this.handlePointerDown)
    },

    watch: {
        text() {
            this.$nextTick(this.measure)
        }
    },

    methods: {
        /**
         * 手机地址栏收缩/展开会频繁触发 resize，
         * 用 debounce 避免 measure 抖动导致文字块位置跳变
         */
        handleResize() {
            if (this._resizeTimer) clearTimeout(this._resizeTimer)
            this._resizeTimer = setTimeout(() => {
                this.measure()
            }, 120)
        },

        measure() {
            const el = this.$refs.block
            if (!el) return

            this.state.maxX = window.innerWidth - el.offsetWidth
            this.state.maxY = window.innerHeight - el.offsetHeight

            // 用 clamp 拉回可视区
            this.state.x = Math.min(Math.max(this.state.x, 0), this.state.maxX)
            this.state.y = Math.min(Math.max(this.state.y, 0), this.state.maxY)

            // 如果速度方向指向墙外，翻一下方向（防止卡在边界）
            if (this.state.x <= 0 && this.vx < 0) this.vx = -this.vx
            if (this.state.x >= this.state.maxX && this.vx > 0) this.vx = -this.vx
            if (this.state.y <= 0 && this.vy < 0) this.vy = -this.vy
            if (this.state.y >= this.state.maxY && this.vy > 0) this.vy = -this.vy

            this.render()
        },

        render() {
            this.$refs.block.style.transform =
                `translate3d(${this.state.x}px, ${this.state.y}px, 0)`
        },

        /** 随机方向（归一化后乘速度） */
        randomDirection() {
            const angle = Math.random() * Math.PI * 2
            this.vx = Math.cos(angle) * this.speed
            this.vy = Math.sin(angle) * this.speed
        },

        /** 点击/触摸屏幕：记录目标点（用于飞行） */
        handlePointerDown(e) {
            const el = this.$refs.block
            this.state.targetX = e.clientX - el.offsetWidth / 2
            this.state.targetY = e.clientY - el.offsetHeight / 2

            // 手机端如果动画被暂停了（例如锁屏回来），确保恢复
            this.start()
        },

        start() {
            if (this.state.rafId) return
            this.state.lastTime = 0
            this.state.rafId = requestAnimationFrame(this.tick)
        },

        stop() {
            if (this.state.rafId) {
                cancelAnimationFrame(this.state.rafId)
                this.state.rafId = null
            }
        },

        handleVisibility() {
            document.hidden ? this.stop() : this.start()
        },

        tick(now) {
            const s = this.state

            if (!s.lastTime) {
                s.lastTime = now
                s.rafId = requestAnimationFrame(this.tick)
                return
            }

            const dt = Math.min((now - s.lastTime) / 1000, 0.05)
            s.lastTime = now

            // ============ 有目标点：朝目标飞 ============
            if (s.targetX !== null && s.targetY !== null) {
                const dx = s.targetX - s.x
                const dy = s.targetY - s.y
                const dist = Math.hypot(dx, dy)

                if (dist < 2) {
                    // 到达目标，恢复漫游
                    s.x = s.targetX
                    s.y = s.targetY
                    s.targetX = null
                    s.targetY = null
                    this.randomDirection()
                } else {
                    // 用 (dx,dy) 归一化后乘以 speed
                    this.vx = (dx / dist) * this.speed
                    this.vy = (dy / dist) * this.speed
                    s.x += this.vx * dt
                    s.y += this.vy * dt

                    // 目标点可能被 resize 拉到边界外，钳制一下
                    s.x = Math.min(Math.max(s.x, 0), s.maxX)
                    s.y = Math.min(Math.max(s.y, 0), s.maxY)

                    // 如果在飞行途中碰到了边界（比如目标点超过了屏幕），
                    // 就取消目标，转为真实反射
                    let hit = false
                    if (s.x <= 0) { s.x = 0; this.vx = Math.abs(this.vx); hit = true }
                    else if (s.x >= s.maxX) { s.x = s.maxX; this.vx = -Math.abs(this.vx); hit = true }
                    if (s.y <= 0) { s.y = 0; this.vy = Math.abs(this.vy); hit = true }
                    else if (s.y >= s.maxY) { s.y = s.maxY; this.vy = -Math.abs(this.vy); hit = true }

                    if (hit) {
                        s.targetX = null
                        s.targetY = null
                    }
                }

                this.render()
                s.rafId = requestAnimationFrame(this.tick)
                return
            }

            // ============ 无目标点：物理反射 ============
            s.x += this.vx * dt
            s.y += this.vy * dt

            let hitX = false
            let hitY = false

            if (s.x <= 0) {
                s.x = 0
                this.vx = Math.abs(this.vx)
                hitX = true
            } else if (s.x >= s.maxX) {
                s.x = s.maxX
                this.vx = -Math.abs(this.vx)
                hitX = true
            }

            if (s.y <= 0) {
                s.y = 0
                this.vy = Math.abs(this.vy)
                hitY = true
            } else if (s.y >= s.maxY) {
                s.y = s.maxY
                this.vy = -Math.abs(this.vy)
                hitY = true
            }

            // 真实反射：只翻转法线方向的速度分量，切向不变
            // 上面已经做了 vx/vy 的翻正，等价于镜面反射
            // 下面加一点点"扰动"，避免长时间在水平/垂直方向来回弹
            if (hitX || hitY) {
                const jitter = 0.06
                // 给切向分量一点随机扰动，视觉更自然
                if (hitX) this.vy += (Math.random() - 0.5) * this.speed * jitter
                if (hitY) this.vx += (Math.random() - 0.5) * this.speed * jitter

                // 重新归一化，保持速度大小恒定
                const mag = Math.hypot(this.vx, this.vy) || 1
                this.vx = (this.vx / mag) * this.speed
                this.vy = (this.vy / mag) * this.speed

                // 再次确保不会朝墙里飞
                if (s.x <= 0 && this.vx < 0) this.vx = -this.vx
                if (s.x >= s.maxX && this.vx > 0) this.vx = -this.vx
                if (s.y <= 0 && this.vy < 0) this.vy = -this.vy
                if (s.y >= s.maxY && this.vy > 0) this.vy = -this.vy
            }

            this.render()
            s.rafId = requestAnimationFrame(this.tick)
        }
    }
}
</script>

<style scoped>
.bouncing-block {
    position: fixed;
    top: 0;
    left: 0;
    padding: 14px 28px;
    background: linear-gradient(145deg, #ff4d6d, #c9184a);
    color: #fff;
    font-size: 1.8rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    white-space: nowrap;
    border-radius: 60px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    box-shadow: 0 0 16px rgba(255, 77, 109, 0.6);
    user-select: none;
    pointer-events: none;

    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    will-change: transform;
}
</style>